import React from 'react';
import { useNavigate } from 'react-router-dom';
import logoMain from '../assets/logo-main.png';
import {
  ShieldCheckIcon,
  UserGroupIcon,
  BuildingOffice2Icon,
  CreditCardIcon,
  DocumentIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  ChartBarIcon,
  BanknotesIcon,
  ReceiptPercentIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/solid';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: BuildingOffice2Icon,
      title: 'Property Groups & Units',
      description:
        'Organize your portfolio into property groups, each with its own units, residents, and dedicated URL (e.g. /your-community/dashboard).',
    },
    {
      icon: CreditCardIcon,
      title: 'Online Rent & Invoice Payments',
      description:
        'Residents can pay invoices online through Stripe Checkout. Payment availability and routing depend on the payment setup for each property.',
    },
    {
      icon: ReceiptPercentIcon,
      title: 'Invoices & PDF Receipts',
      description:
        'Create one-time or recurring invoices per resident, record offline payments manually, and download professional A4 PDF invoices generated server-side.',
    },
    {
      icon: ChartBarIcon,
      title: 'Cash Flow & Reporting',
      description:
        'A dedicated Cash Flow view tracks rent income, monthly debt obligations (mortgage, insurance, taxes), and one-time expenses per property — with monthly and annual breakdowns.',
    },
    {
      icon: BanknotesIcon,
      title: 'Expense Tracking',
      description:
        'Log dated expenses by category (repairs, utilities, landscaping, supplies, etc.), tie them to a specific property or your whole portfolio, and see them roll into your net cash flow.',
    },
    {
      icon: UserGroupIcon,
      title: 'Role-Based Access',
      description:
        'Separate, permission-scoped experiences for property managers, residents, and vendors — with strict server-enforced tenant isolation between property groups.',
    },
    {
      icon: ExclamationTriangleIcon,
      title: 'Maintenance Requests',
      description:
        'Residents submit issues; admins track them through Open → In Progress → Resolved with notes and history.',
    },
    {
      icon: DocumentIcon,
      title: 'Documents & Announcements',
      description:
        'Upload and share documents with your community, and post announcements that residents see on their dashboard.',
    },
    {
      icon: CalendarIcon,
      title: 'Community Events',
      description:
        'Publish categorized community events that residents can browse from their dashboard.',
    },
    {
      icon: WrenchScrewdriverIcon,
      title: 'Vendor Directory',
      description:
        'Maintain a vendor directory with services, rates, and contact details. Vendors can manage their profile and service listings when given access.',
    },
    {
      icon: BanknotesIcon,
      title: 'Rent Schedules',
      description:
        'Set per-resident rent amounts and recurring schedules (weekly, bi-weekly, monthly, quarterly, yearly) with optional end dates that drive your cash flow projections.',
    },
  ];

  const benefits = [
    {
      title: 'For Property Managers',
      description:
        'Run billing, payments, expenses, and cash flow for multiple property groups from one dashboard.',
      icon: ShieldCheckIcon,
    },
    {
      title: 'For Residents',
      description:
        'Pay invoices online, download receipts, submit maintenance requests, and stay current on community announcements and events.',
      icon: UserGroupIcon,
    },
    {
      title: 'For Vendors',
      description:
        'Get listed in your client communities’ vendor directories with your services, rates, and contact details.',
      icon: BuildingOffice2Icon,
    },
  ];

  return (
    <div className="min-h-screen bg-background-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-full blur-3xl"></div>
      </div>

      {/* Nav + Hero fill the full viewport height on desktop (lg+),
          while smaller screens keep their natural flow */}
      <div className="lg:min-h-screen lg:flex lg:flex-col">
      {/* Navigation */}
      <nav className="relative z-10 p-6 glass-morphism m-4 mb-0">
        <div className="max-w-7xl xl:max-w-[100rem] 2xl:max-w-[115rem] mx-auto flex justify-between items-center">
          <div className="flex items-center">
            <img src={logoMain} alt="BlockBoard" className="h-10 w-auto object-contain" />
          </div>
          <div className="flex space-x-4">
            <button
              onClick={() => navigate('/pricing')}
              className="text-white/80 hover:text-white transition-colors px-4 py-2"
            >
              Pricing
            </button>
            <button onClick={() => navigate('/login')} className="btn-primary">
              Sign In
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 max-w-7xl xl:max-w-[100rem] 2xl:max-w-[115rem] mx-auto px-6 py-20 text-center lg:flex-1 lg:flex lg:flex-col lg:justify-center lg:py-0 w-full">
        <div className="animate-fade-in">
          <h1 className="text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-white/90 to-white/70 bg-clip-text text-transparent leading-tight">
            Billing, payments, and
            <br />
            <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              property managers & HOAs
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-white/70 mb-8 max-w-3xl lg:max-w-4xl xl:max-w-5xl mx-auto leading-relaxed">
            BlockBoard helps independent landlords, small property management companies, and HOA
            boards invoice residents, accept online payments through Stripe Checkout, and track the
            financial activity of every property — all from one dashboard.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/signup')}
              className="group btn-primary text-lg px-8 py-4 flex items-center justify-center space-x-2"
            >
              <span>Get Started</span>
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <button
              onClick={() => navigate('/pricing')}
              className="text-lg px-8 py-4 rounded-xl border border-white/20 text-white/80 hover:text-white hover:border-white/40 transition-all"
            >
              See Pricing
            </button>
          </div>
        </div>
      </section>
      </div>

      {/* Features Section */}
      <section className="relative z-10 max-w-7xl xl:max-w-[100rem] 2xl:max-w-[115rem] mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">What's actually in the box</h2>
          <p className="text-xl text-white/70 max-w-2xl mx-auto">
            Practical tools for invoicing, property operations, community communication, and financial tracking.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="glass-card p-8 group hover:scale-105 transition-all duration-300"
            >
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-primary to-secondary flex items-center justify-center mb-6 shadow-futuristic group-hover:shadow-futuristic group-hover:-translate-y-1 transition-all duration-300">
                <feature.icon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">{feature.title}</h3>
              <p className="text-white/70 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative z-10 max-w-7xl xl:max-w-[100rem] 2xl:max-w-[115rem] mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Built for three roles</h2>
          <p className="text-xl text-white/70 max-w-2xl mx-auto">
            Each role gets its own dashboard and only sees what's relevant to them.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="glass-card p-10 text-center group hover:scale-105 transition-all duration-300"
            >
              <div className="w-20 h-20 rounded-3xl bg-gradient-to-r from-primary to-secondary flex items-center justify-center mb-8 mx-auto shadow-futuristic group-hover:shadow-futuristic group-hover:-translate-y-1 transition-all duration-300">
                <benefit.icon className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-4">{benefit.title}</h3>
              <p className="text-white/70 text-lg leading-relaxed">{benefit.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 max-w-7xl xl:max-w-[100rem] 2xl:max-w-[115rem] mx-auto px-6 py-20">
        <div className="glass-card p-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Bring your property operations into one place
          </h2>
          <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto">
            Start a trial, add your first property group, and begin managing invoices, residents,
            maintenance, documents, and expenses from one dashboard.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/signup')}
              className="group btn-primary text-lg px-8 py-4 flex items-center justify-center space-x-2"
            >
              <span>Get Started</span>
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 glass-morphism m-4 mt-0 p-8">
        <div className="max-w-7xl xl:max-w-[100rem] 2xl:max-w-[115rem] mx-auto text-center">
          <div className="flex items-center justify-center mb-4">
            <img src={logoMain} alt="BlockBoard" className="h-8 w-auto object-contain" />
          </div>
          <p className="text-white/60 text-sm">
            © 2025 BlockBoard. Property and HOA management for independent landlords and small portfolios.
          </p>
          <p className="text-white/40 text-xs mt-2 max-w-2xl mx-auto">
            BlockBoard is a software platform and is not a property manager, financial institution,
            law firm, tenant screening service, or compliance provider.
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <button
              onClick={() => navigate('/terms')}
              className="text-white/60 hover:text-white text-sm transition-colors duration-200"
            >
              Terms of Service
            </button>
            <button
              onClick={() => navigate('/privacy')}
              className="text-white/60 hover:text-white text-sm transition-colors duration-200"
            >
              Privacy Policy
            </button>
            <button
              onClick={() => navigate('/security')}
              className="text-white/60 hover:text-white text-sm transition-colors duration-200"
            >
              Security
            </button>
            <button
              onClick={() => navigate('/pricing')}
              className="text-white/60 hover:text-white text-sm transition-colors duration-200"
            >
              Pricing
            </button>
          </div>
          <div className="mt-4">
            <button
              onClick={() => navigate('/admin/login')}
              className="text-white/40 hover:text-white/70 text-xs transition-colors duration-200"
            >
              Admin
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
